import React from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

// Which identifier leads on the chart — moved out of the sidebar's CONFIGURATIONS
// block into the Pedigree labels modal (TGCLOUD-1674). It is the one part of that
// block the modal did not already cover, and it belongs with the other label
// settings rather than in a panel called "Family record".
//
// Its own teal is the darker #1a7a8a, not the #2BA9BF used elsewhere in the modal.

const TEAL = '#1a7a8a';
const TEAL_BG = 'rgba(43, 169, 191, 0.08)';
const TEAL_BORDER = 'rgba(43, 169, 191, 0.5)';

// Two-line radio label: title + grey sublabel.
const TwoLineLabel = ({ title, sub }) => (
  <Box>
    <Typography sx={{ fontSize: 14, lineHeight: 1.25 }}>{title}</Typography>
    <Typography
      sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.25 }}
    >
      {sub}
    </Typography>
  </Box>
);

// Preview card for a display label mode.
//
// TGCLOUD-1666: these cards used to hardcode `F-12345` / `G-8-00014`, which is what produced the
// report that "the numbers do not match anything" — a mockup reads as a preview, so the user could
// not tell which identifier the mode would actually surface. They now carry THIS family's real
// G number and real primary external ID, mirroring exactly what the chart header draws.
const DisplayLabelPreview = ({ mode, active, gNumber, externalId }) => {
  const isExternal = mode === 'primary-external';
  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        borderRadius: 1,
        border: '1px solid',
        borderColor: active ? TEAL_BORDER : 'divider',
        backgroundColor: active ? TEAL_BG : '#fafafa',
        p: 1.25,
        transition: 'background-color 120ms, border-color 120ms'
      }}
    >
      {active && (
        <CheckIcon
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            fontSize: 14,
            color: TEAL
          }}
        />
      )}
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.disabled'
        }}
      >
        {isExternal ? 'External ID' : 'G number'}
      </Typography>
      {/* A site's own identifier can be long, and both lines are nowrap+ellipsis — so each carries
          a `title`, the same affordance the chart header gives its headline and chips. */}
      <Typography
        title={isExternal ? externalId : gNumber}
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: TEAL,
          mt: 0.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {isExternal ? externalId : gNumber}
      </Typography>
      <Typography
        title={isExternal ? gNumber : externalId}
        sx={{
          fontSize: 11,
          color: 'text.secondary',
          mt: 0.25,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {isExternal ? `${gNumber} · Master ID` : `AKA ${externalId}`}
      </Typography>
    </Box>
  );
};

/**
 * The Display label control.
 *
 * Rendered ONLY when there is a genuine choice to make (TGCLOUD-1666):
 *  - a family with no external ID has nothing to promote, and the original
 *    "always render, external option disabled behind a tooltip" version is what
 *    produced the report that the section could not be configured;
 *  - on an anonymous-pedigrees deployment the mode is clamped to the G number,
 *    so the radio group would degenerate to a single option.
 *
 * Hiding it deliberately does NOT reset `labelsSet.displayLabelMode` — a preset or
 * group default may legitimately carry 'primary-external', and it must keep
 * round-tripping through a family that happens to have no external ID today.
 *
 * `externalFamilyIds` is not stripped by the backend anonymous layer (only the
 * per-person externalPatientId is), so the two gates are independent.
 */
const DisplayLabelSection = ({
  labelsSet,
  setLabelsSet,
  anonymous,
  gNumber,
  primaryExternalId,
  hasExternalIds,
  SectionLabel
}) => {
  if (anonymous || !hasExternalIds) return null;

  const displayLabelMode = anonymous
    ? 'g-number'
    : labelsSet?.displayLabelMode || 'g-number';

  const handleChange = (e) =>
    setLabelsSet((prev) => ({ ...prev, displayLabelMode: e.target.value }));

  return (
    <>
      <Divider />
      <Box>
        <SectionLabel data-va-anchor="pedigree.labels.displayMode">
          Display label
        </SectionLabel>
        <RadioGroup value={displayLabelMode} onChange={handleChange}>
          <FormControlLabel
            value="g-number"
            control={<Radio size="small" />}
            label={
              <TwoLineLabel
                title="G number (master ID)"
                sub="G number shown as primary"
              />
            }
            sx={{
              mx: 0,
              my: 0,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': { mt: 0.25 }
            }}
          />
          <FormControlLabel
            value="primary-external"
            control={<Radio size="small" />}
            label={
              <TwoLineLabel
                title="Primary external ID"
                sub="External alias shown as primary"
              />
            }
            sx={{
              mx: 0,
              my: 0,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': { mt: 0.25 }
            }}
          />
        </RadioGroup>
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <DisplayLabelPreview
            mode="g-number"
            active={displayLabelMode === 'g-number'}
            gNumber={gNumber}
            externalId={primaryExternalId}
          />
          <DisplayLabelPreview
            mode="primary-external"
            active={displayLabelMode === 'primary-external'}
            gNumber={gNumber}
            externalId={primaryExternalId}
          />
        </Box>
        <Typography
          sx={{
            mt: 1.25,
            fontSize: 12,
            color: 'text.secondary',
            lineHeight: 1.4
          }}
        >
          The primary external ID is the starred entry under EXTERNAL IDS.
        </Typography>
      </Box>
    </>
  );
};

export default DisplayLabelSection;
